(() => {
  if (window.CAJBundlePremiumV1) return;
  window.CAJBundlePremiumV1 = true;

  const states = new WeakMap();

  const money = (cents, currency) => {
    const value = Number(cents || 0) / 100;
    return `${value.toLocaleString('en-EG', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${currency || 'EGP'}`;
  };

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const getProductInfo = (root) => root.closest('product-info') || root.closest('.product') || document;
  const getForm = (root) => {
    const scope = getProductInfo(root);
    return scope.querySelector('product-form form') || scope.querySelector('form[action*="/cart/add"]');
  };

  const requiredExtras = (state) => Math.max(0, state.tier - 1);
  const isComplete = (state) => state.selections.length === requiredExtras(state);
  const discountRate = (tier) => tier === 2 ? 0.10 : tier === 3 ? 0.15 : 0;

  const currentVariant = (root, state) => {
    const form = getForm(root);
    const id = String(form?.querySelector('[name="id"]')?.value || '');
    return { id, ...(state.currentVariants[id] || {}) };
  };

  const currentPrice = (root, state) => Number(currentVariant(root, state).price || 0);

  const totals = (root, state) => {
    const subtotal = currentPrice(root, state) + state.selections.reduce((sum, item) => sum + Number(item.priceCents || 0), 0);
    const saving = Math.round(subtotal * discountRate(state.tier));
    return { subtotal, saving, total: Math.max(0, subtotal - saving) };
  };

  const tierSavingCents = (root, state, tier) => {
    if (tier < 2) return 0;
    const base = currentPrice(root, state);
    if (!base) return 0;
    const extrasNeeded = tier - 1;
    const chosen = tier === state.tier ? state.selections.slice(0, extrasNeeded) : [];
    const selectedTotal = chosen.reduce((sum, item) => sum + Number(item.priceCents || 0), 0);
    const missing = Math.max(0, extrasNeeded - chosen.length);
    const estimatedSubtotal = base + selectedTotal + (base * missing);
    return Math.round(estimatedSubtotal * discountRate(tier));
  };

  const setError = (root, message = '') => {
    const box = root.querySelector('[data-caj-bundle-error]');
    if (!box) return;
    box.textContent = message;
    box.hidden = !message;
  };

  const setPickerMessage = (root, message = '') => {
    const box = root.querySelector('[data-caj-picker-message]');
    if (!box) return;
    box.textContent = message;
    box.hidden = !message;
  };

  const updateSavings = (root, state) => {
    [2, 3].forEach((tier) => {
      const el = root.querySelector(`[data-caj-tier-save="${tier}"]`);
      if (!el) return;
      const cents = tierSavingCents(root, state, tier);
      el.textContent = cents ? `YOU SAVE ${money(cents, state.currency)}` : 'YOU SAVE —';
    });

    const pickerSaving = root.querySelector('[data-caj-picker-saving]');
    if (pickerSaving && state.tier > 1) {
      const cents = tierSavingCents(root, state, state.tier);
      pickerSaving.textContent = cents ? `YOU SAVE ${money(cents, state.currency)}` : 'YOU SAVE —';
    }

    const onePrice = root.querySelector('[data-caj-one-price]');
    const variant = currentVariant(root, state);
    if (onePrice && variant.money) onePrice.textContent = variant.money;
  };

  const renderSummary = (root, state) => {
    const summary = root.querySelector('[data-caj-summary]');
    const change = root.querySelector('[data-caj-open-picker]');
    if (!summary || !change) return;

    if (state.tier === 1) {
      summary.hidden = true;
      summary.innerHTML = '';
      change.hidden = true;
      updateSavings(root, state);
      return;
    }

    const variant = currentVariant(root, state);
    const rows = [
      '<div class="caj-bundle-v2__summary-title"><span>YOUR BUNDLE</span></div>',
      `<div class="caj-bundle-v2__summary-row"><span><b>1.</b> ${escapeHtml(state.productTitle)}${variant.title && variant.title !== 'Default Title' ? ` — ${escapeHtml(variant.title)}` : ''}</span><strong>${variant.price ? money(variant.price, state.currency) : ''}</strong></div>`,
    ];

    state.selections.forEach((item, index) => {
      rows.push(`<div class="caj-bundle-v2__summary-row"><span><b>${index + 2}.</b> ${escapeHtml(item.productTitle)}${item.variantTitle && item.variantTitle !== 'Default Title' ? ` — ${escapeHtml(item.variantTitle)}` : ''}</span><strong>${money(item.priceCents, state.currency)}</strong></div>`);
    });

    const missing = requiredExtras(state) - state.selections.length;
    if (missing > 0) {
      rows.push(`<div class="caj-bundle-v2__summary-empty">Choose ${missing} more item${missing > 1 ? 's' : ''} to complete your bundle.</div>`);
    } else {
      const value = totals(root, state);
      rows.push(`
        <div class="caj-bundle-v2__totals">
          <div class="caj-bundle-v2__total-row"><span>Subtotal</span><strong>${money(value.subtotal, state.currency)}</strong></div>
          <div class="caj-bundle-v2__total-row caj-bundle-v2__total-row--saving"><span>Bundle saving</span><strong>− ${money(value.saving, state.currency)}</strong></div>
          <div class="caj-bundle-v2__total-row caj-bundle-v2__total-row--grand"><span>Bundle total</span><strong>${money(value.total, state.currency)}</strong></div>
        </div>
      `);
    }

    summary.innerHTML = rows.join('');
    summary.hidden = false;
    change.hidden = !isComplete(state);
    updateSavings(root, state);
  };

  const renderSelected = (root, state) => {
    const selected = root.querySelector('[data-caj-picker-selected]');
    const count = root.querySelector('[data-caj-selected-count]');
    const needed = requiredExtras(state);
    if (count) count.textContent = `${state.selections.length} / ${needed} selected`;
    if (!selected) return;

    if (!state.selections.length) {
      selected.hidden = true;
      selected.innerHTML = '';
      return;
    }

    selected.hidden = false;
    selected.innerHTML = state.selections.map((item, index) => `
      <div class="caj-bundle-picker__selected-item">
        <span>${escapeHtml(item.productTitle)}${item.variantTitle && item.variantTitle !== 'Default Title' ? ` — ${escapeHtml(item.variantTitle)}` : ''}</span>
        <button type="button" data-caj-remove-selection="${index}" aria-label="Remove ${escapeHtml(item.productTitle)}">REMOVE</button>
      </div>
    `).join('');
  };

  const renderCards = (root, state) => {
    const grid = root.querySelector('[data-caj-products-grid]');
    if (!grid) return;

    const query = String(root.querySelector('[data-caj-product-search]')?.value || '').trim().toLowerCase();
    const products = state.products.filter((product) =>
      Array.isArray(product.variants) && product.variants.length && (!query || String(product.title).toLowerCase().includes(query))
    );

    if (!products.length) {
      grid.innerHTML = '<div class="caj-bundle-picker__no-results">No products found.</div>';
      return;
    }

    const full = state.selections.length >= requiredExtras(state);
    grid.innerHTML = products.map((product) => {
      const variants = product.variants;
      const first = variants[0];
      const hasChoices = variants.length > 1 || first?.title !== 'Default Title';
      return `
        <article class="caj-bundle-product" data-caj-product-card data-product-id="${product.id}">
          <div class="caj-bundle-product__image">
            ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy" decoding="async">` : '<span class="caj-bundle-product__placeholder">CAJ</span>'}
          </div>
          <div class="caj-bundle-product__body">
            <div class="caj-bundle-product__meta">
              <h3>${escapeHtml(product.title)}</h3>
              <div class="caj-bundle-product__price" data-caj-card-price>${money(first.price, state.currency)}</div>
            </div>
            <div class="caj-bundle-product__options">
              ${hasChoices ? `
                <span class="caj-bundle-product__option-label">CHOOSE YOUR OPTION</span>
                <select data-caj-card-variant aria-label="Choose option for ${escapeHtml(product.title)}">
                  ${variants.map((variant) => `<option value="${variant.id}" data-price-cents="${variant.price}" data-variant-title="${escapeHtml(variant.title)}">${escapeHtml(variant.title)} — ${money(variant.price, state.currency)}</option>`).join('')}
                </select>
              ` : `<input type="hidden" data-caj-card-variant value="${first.id}" data-price-cents="${first.price}" data-variant-title="${escapeHtml(first.title)}">`}
              <button type="button" class="caj-bundle-product__add" data-caj-add-product data-caj-bundle-full="${full ? 'true' : 'false'}" ${full ? 'disabled' : ''}>${full ? 'BUNDLE FULL' : 'ADD TO BUNDLE'}</button>
            </div>
          </div>
        </article>
      `;
    }).join('');
  };

  const openPicker = (root, state, shouldScroll = false) => {
    if (state.tier < 2) return;
    const picker = root.querySelector('[data-caj-bundle-picker]');
    if (!picker) return;
    picker.hidden = false;
    setPickerMessage(root, '');
    renderSelected(root, state);
    renderCards(root, state);
    updateSavings(root, state);
    if (shouldScroll) window.setTimeout(() => picker.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 30);
  };

  const closePicker = (root) => {
    const picker = root.querySelector('[data-caj-bundle-picker]');
    if (picker) picker.hidden = true;
  };

  const updateCheckoutVisibility = (root, state) => {
    const scope = getProductInfo(root);
    scope.querySelectorAll('.shopify-payment-button, [data-shopify="payment-button"]').forEach((el) => {
      if (!el.dataset.cajBundleDisplayStored) {
        el.dataset.cajBundleDisplayStored = '1';
        el.dataset.cajBundleOriginalDisplay = el.style.display || '__empty__';
      }
      if (state.tier > 1) el.style.display = 'none';
      else el.style.display = el.dataset.cajBundleOriginalDisplay === '__empty__' ? '' : el.dataset.cajBundleOriginalDisplay;
    });
  };

  const setTier = (root, state, tier, open = true) => {
    state.tier = tier;
    const max = requiredExtras(state);
    if (state.selections.length > max) state.selections = state.selections.slice(0, max);
    if (tier === 1) state.selections = [];

    root.querySelectorAll('[data-caj-tier]').forEach((button) => {
      const active = Number(button.dataset.cajTier) === tier;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    setError(root, '');
    setPickerMessage(root, '');
    updateCheckoutVisibility(root, state);
    renderSelected(root, state);
    renderSummary(root, state);

    if (tier > 1 && open) openPicker(root, state, true);
    else if (tier === 1) closePicker(root);
  };

  const addSelectionFromCard = (root, state, card) => {
    if (state.selections.length >= requiredExtras(state)) {
      setPickerMessage(root, 'Your bundle is full. Remove an item to change it.');
      return;
    }

    const control = card?.querySelector('[data-caj-card-variant]');
    if (!control) return;
    const product = state.products.find((item) => String(item.id) === String(card.dataset.productId));
    const variant = product?.variants?.find((item) => String(item.id) === String(control.value));
    if (!product || !variant) return;

    state.selections.push({
      uid: `${Date.now()}-${Math.random()}`,
      productId: String(product.id),
      productTitle: product.title,
      variantId: String(variant.id),
      variantTitle: variant.title,
      priceCents: Number(variant.price || 0),
    });

    setPickerMessage(root, '');
    renderSelected(root, state);
    renderSummary(root, state);
    updateSavings(root, state);

    if (isComplete(state)) closePicker(root);
    else renderCards(root, state);
  };

  const setLoading = (form, loading) => {
    const submit = form?.querySelector('[type="submit"]');
    const spinner = submit?.querySelector('.loading__spinner');
    if (!submit) return;
    if (loading) submit.setAttribute('aria-disabled', 'true');
    else submit.removeAttribute('aria-disabled');
    submit.classList.toggle('loading', loading);
    spinner?.classList.toggle('hidden', !loading);
  };

  const addBundleToCart = async (root, state, form) => {
    if (state.adding) return;

    if (!isComplete(state)) {
      const missing = requiredExtras(state) - state.selections.length;
      setError(root, `Choose ${missing} more item${missing > 1 ? 's' : ''} for your bundle.`);
      openPicker(root, state, true);
      return;
    }

    const variant = currentVariant(root, state);
    if (!variant.id) {
      setError(root, 'Please choose an available option first.');
      return;
    }

    state.adding = true;
    setLoading(form, true);
    setError(root, '');

    const bundleId = `caj-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    const rawItems = [
      { id: Number(variant.id), quantity: 1 },
      ...state.selections.map((item) => ({ id: Number(item.variantId), quantity: 1 })),
    ];
    const items = rawItems.map((item, index) => ({
      ...item,
      properties: {
        '_CAJ Bundle': bundleId,
        '_CAJ Bundle Size': `${state.tier} pieces`,
        '_CAJ Bundle Slot': String(index + 1),
      },
    }));

    const cartDrawer = document.querySelector('cart-drawer');
    const payload = { items };
    if (cartDrawer?.getSectionsToRender) {
      payload.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
      cartDrawer.setActiveElement?.(document.activeElement);
    }

    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add bundle to cart.');

      if (cartDrawer?.renderContents && data.sections) {
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(data);
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
      }
    } catch (error) {
      console.error('CAJ bundle add error', error);
      setError(root, error?.message || 'Could not add bundle to cart. Please try again.');
    } finally {
      state.adding = false;
      setLoading(form, false);
    }
  };

  const init = (root) => {
    if (!root || root.dataset.cajBundleReady === '1') return;
    root.dataset.cajBundleReady = '1';

    let currentVariants = {};
    let products = [];
    try { currentVariants = JSON.parse(root.querySelector('[data-caj-current-variants]')?.textContent || '{}'); } catch (_) {}
    try { products = JSON.parse(root.querySelector('[data-caj-bundle-products]')?.textContent || '[]'); } catch (_) {}

    const state = {
      tier: 1,
      selections: [],
      products,
      currentVariants,
      currency: root.dataset.currency || 'EGP',
      productTitle: root.dataset.productTitle || '',
      adding: false,
    };
    states.set(root, state);

    const form = getForm(root);
    if (form) {
      const quantity = form.querySelector('[name="quantity"]');
      if (quantity) quantity.value = '1';
      form.addEventListener('submit', (event) => {
        if (state.tier === 1) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (state.adding) return;
        addBundleToCart(root, state, form);
      }, true);
    }

    const quantityBlock = getProductInfo(root).querySelector('.product-form__quantity');
    if (quantityBlock) quantityBlock.style.display = 'none';

    root.addEventListener('click', (event) => {
      const tierButton = event.target.closest('[data-caj-tier]');
      if (tierButton) {
        setTier(root, state, Number(tierButton.dataset.cajTier), true);
        return;
      }
      if (event.target.closest('[data-caj-open-picker]')) {
        openPicker(root, state, true);
        return;
      }
      if (event.target.closest('[data-caj-close-picker]')) {
        closePicker(root);
        return;
      }
      const remove = event.target.closest('[data-caj-remove-selection]');
      if (remove) {
        state.selections.splice(Number(remove.dataset.cajRemoveSelection), 1);
        renderSelected(root, state);
        renderSummary(root, state);
        openPicker(root, state, false);
        return;
      }
      const add = event.target.closest('[data-caj-add-product]');
      if (add && !add.disabled) {
        addSelectionFromCard(root, state, add.closest('[data-caj-product-card]'));
      }
    });

    root.addEventListener('input', (event) => {
      if (event.target.matches('[data-caj-product-search]')) renderCards(root, state);
    });

    root.addEventListener('change', (event) => {
      if (!event.target.matches('select[data-caj-card-variant]')) return;
      const option = event.target.selectedOptions[0];
      const price = event.target.closest('[data-caj-product-card]')?.querySelector('[data-caj-card-price]');
      if (price && option) price.textContent = money(Number(option.dataset.priceCents || 0), state.currency);
    });

    document.addEventListener('change', (event) => {
      if (!getProductInfo(root).contains(event.target)) return;
      if (event.target.closest('[data-caj-bundle-v2]')) return;
      window.setTimeout(() => {
        const liveForm = getForm(root);
        const quantity = liveForm?.querySelector('[name="quantity"]');
        if (quantity) quantity.value = '1';
        renderSummary(root, state);
        updateSavings(root, state);
      }, 80);
    });

    setTier(root, state, 1, false);
    updateSavings(root, state);
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-caj-bundle-v2]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
